"use client";

import { NavMain } from "@/components/nav-main";
import { NavUser } from "@/components/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from "@/components/ui/sidebar";
import { usePathname } from "next/navigation";
import { hasNoSidebar } from "@/lib/sidebar-routes";
import Image from "next/image";
import { useMyUser } from "@/hooks/auth/use-my-user";
import { useCallback, useMemo } from "react";
import { useMainLayout } from "@/hooks/use-main-layout";
import { NavigationItem } from "@/services/main-layout.service";
import Link from "next/link";
import { useCurrentEnterpriseCustomer } from "@/hooks/use-current-enterprise-customer";
import { useEntityStore } from "@/stores/entity.store";
import { useEntityPermissionsMap } from "@/hooks/schema/use-my-permissions";
import { RenderIcon } from "../render-icon";
import { cn } from "@/lib/utils";
import { DashboardsNavFolder } from "./dashboards-nav-folder";

const otherFeaturesNavItems: Array<NavItem> = [
  {
    title: "Forms",
    url: "/p/forms",
    icon: "FileText",
    id: "forms",
    type: "entity",
  },
  {
    title: "Flows",
    url: "/p/flows",
    icon: "Workflow",
    id: "flows",
    type: "entity",
  },
  {
    title: "Media",
    url: "/p/media",
    icon: "Images",
    id: "media",
    type: "entity",
  },
  // Hidden until agent write approvals ship; the /p/proposals route still works directly.
  // {
  //   title: "Proposals",
  //   url: "/p/proposals",
  //   icon: "ClipboardCheck",
  //   id: "requests",
  //   type: "entity",
  // },
  {
    title: "Dashboards",
    url: "/p/dashboard",
    icon: "LayoutDashboard",
    id: "dashboards",
    type: "entity",
    // Opt-in feature: only shown once the "Dashboard" entity exists (i.e. the
    // feature has been activated in Settings → Features). See template.service.ts.
    requiresEntity: "Dashboard",
  },
  // Hidden from the main nav while still in development. The page is still
  // reachable via the direct URL /p/events.
  // {
  //   title: 'Events',
  //   url: '/p/events',
  //   icon: 'Activity',
  //   id: 'events',
  //   type: 'entity',
  // },
];

export interface NavItem {
  title: string;
  url: string;
  icon?: string;
  children?: Array<NavItem>;
  comingSoon?: boolean;
  active?: boolean;
  id: string;
  type: "entity" | "folder";
  parentId?: string;
  /** Gate this item on a custom entity existing (opt-in feature). */
  requiresEntity?: string;
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { myUser } = useMyUser();

  const { mainLayout } = useMainLayout();
  const { entityPermissionsMap, hasLoaded: permissionsLoaded } = useEntityPermissionsMap();

  const { logo, appName, companyName } = useCurrentEnterpriseCustomer();

  const pathname = usePathname();

  const { getEntityByName } = useEntityStore();

  const canShowOtherFeature = useCallback(
    (item: NavItem): boolean => {
      if (!item.requiresEntity) {
        return true;
      }

      const entity = getEntityByName(item.requiresEntity);
      if (!entity) {
        return false;
      }

      return entityPermissionsMap?.get(entity.id)?.has_read ?? false;
    },
    [entityPermissionsMap, getEntityByName],
  );

  const hasNavigableItems = useCallback(
    (item: NavigationItem): boolean => {
      // If it's an entity and we have permission to read it, it's navigable
      if (item.type === "entity") {
        if (!item.name) return false;

        const entity = getEntityByName(item.name);

        if (!entity) {
          return false;
        }

        const permission = entityPermissionsMap?.get(entity.id);

        return permission?.has_read ?? false;
      }

      // If it's a folder, check if it has any navigable children
      if (item.type === "folder") {
        // Check direct children from folder_structure
        const directChildren = (mainLayout?.folder_structure as Array<NavigationItem>)?.filter(
          (i) => i.parentId === item.id,
        );

        if (directChildren?.some((child) => hasNavigableItems(child))) {
          return true;
        }

        // Check nested navigation_items
        if (item.navigation_items?.data?.length) {
          return item.navigation_items.data.some((child) => hasNavigableItems(child));
        }
      }

      return false;
    },
    [getEntityByName, mainLayout?.folder_structure, entityPermissionsMap],
  );

  const createNavItem = useCallback(
    (item: NavigationItem): NavItem | null => {
      if (item.show_in_nav === false) return null;

      // For folders, check if they have any navigable items
      if (item.type === "folder" && !hasNavigableItems(item)) {
        return null;
      }

      const itemUrl = `/${item.name}`;
      const isActive = pathname === itemUrl || pathname.startsWith(`${itemUrl}/`);

      const children = (mainLayout?.folder_structure as Array<NavigationItem>)
        ?.filter((i) => i.parentId === item.id)
        ?.map(createNavItem);

      const entity = item.name ? getEntityByName(item.name) : undefined;

      if (item.type === "entity" && !entity) {
        return null;
      }

      const permission = entityPermissionsMap?.get(entity?.id ?? "");

      if (item.type === "entity" && !permission?.has_read) {
        return null;
      }

      return {
        title: item.name ?? "",
        url: itemUrl,
        icon: item.icon,
        children: children?.filter(Boolean) as NavItem[],
        active: isActive,
        id: item.id,
        type: item.type ?? "entity",
        ...(item.navigation_items?.data?.length
          ? {
              children: item.navigation_items.data
                .map(createNavItem)
                .filter((n) => !!n?.title)
                .filter(Boolean) as NavItem[],
            }
          : {}),
      };
    },
    [
      getEntityByName,
      mainLayout?.folder_structure,
      pathname,
      entityPermissionsMap,
      hasNavigableItems,
    ],
  );

  const navItems: Array<NavItem> = useMemo(() => {
    if (
      typeof mainLayout?.folder_structure === "object" &&
      Array.isArray(mainLayout?.folder_structure)
    ) {
      return mainLayout.folder_structure
        .filter((i) => i.parentId === null)
        .map(createNavItem)
        .filter((n) => !!n?.title)
        .filter(Boolean) as NavItem[];
    }
    return [];
  }, [createNavItem, mainLayout?.folder_structure]);

  const userName = useMemo(() => {
    return `${myUser?.current_user?.data?.user_profile?.data?.firstname ?? ""} ${myUser?.current_user?.data?.user_profile?.data?.lastname ?? ""}`.trim();
  }, [myUser]);

  if (hasNoSidebar(pathname) || pathname.includes("/p/settings")) {
    return null;
  }

  // Gate the whole sidebar body on BOTH the layout and permissions being ready.
  // Without the permissions gate, the layout arrives first, every entity nav item
  // is filtered out (no has_read yet), and the "Apps" section (Forms/Flows/
  // Dashboards) renders alone — a half-populated sidebar. Waiting for permissions
  // makes the entity items and the Apps section appear together.
  const isLoading = !mainLayout || !permissionsLoaded;

  return (
    <Sidebar variant="inset" {...props} className="h-full">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href="/">
                <div className="h-9 w-9">
                  <Image
                    src={logo}
                    alt="logo of the current enterprise customer"
                    className="h-full w-full object-contain"
                    width={35}
                    height={35}
                  />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{appName}</span>
                  <span className="truncate text-xs">by {companyName}</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="">
        {isLoading ? (
          <SidebarGroup>
            <SidebarMenu>
              {Array.from({ length: 5 }).map((_, i) => (
                <SidebarMenuSkeleton key={i} showIcon />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ) : (
          navItems.length > 0 && <NavMain items={navItems} />
        )}
        {!isLoading && (
          <SidebarGroup>
            <SidebarGroupLabel>Apps</SidebarGroupLabel>
            <SidebarMenu>
              {otherFeaturesNavItems
                .filter(canShowOtherFeature)
                .map((item) =>
                  item.id === "dashboards" ? (
                    <DashboardsNavFolder key={item.id} item={item} />
                  ) : (
                    <OtherFeaturesNavItem key={item.id} item={item} />
                  ),
                )}
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter>
        <NavUser
          user={{
            name: userName,
            email: myUser?.current_user?.data?.email ?? "",
            avatar: "",
          }}
        />
      </SidebarFooter>
    </Sidebar>
  );
}

const OtherFeaturesNavItem = ({ item }: { item: NavItem }) => {
  const pathname = usePathname();
  const isActive = pathname.startsWith(item.url);

  return (
    <SidebarMenuItem key={item.id}>
      <SidebarMenuButton asChild>
        <Link
          href={item.url}
          className={cn("text-muted-foreground", { "bg-sidebar-accent": isActive })}
          suppressHydrationWarning
        >
          <RenderIcon iconName={item.icon ?? ""} size={14} className="text-muted-foreground" />
          {item.title}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
